<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Tempo de Resposta',
  },
});

const distribution = computed(() => props.data?.distribution || []);
const byHour = computed(() => props.data?.by_hour || []);
const byAgent = computed(() => props.data?.by_agent || []);

const maxDistribution = computed(() => {
  return Math.max(...distribution.value.map(d => d.count), 1);
});

function formatTime(minutes) {
  if (!minutes || minutes === 0) return '0 min';
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  return `${(minutes / 60).toFixed(1)} h`;
}

function getTimeColor(minutes) {
  if (minutes <= 5) return 'text-n-teal-11';
  if (minutes <= 15) return 'text-n-blue-11';
  if (minutes <= 30) return 'text-n-amber-11';
  return 'text-n-ruby-11';
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-timer text-lg text-n-blue-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-64">
      <Spinner size="large" />
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- Summary Stats -->
      <div class="grid grid-cols-4 gap-3 text-center">
        <div class="p-3 rounded-lg bg-n-alpha-1">
          <div
            class="text-xl font-bold"
            :class="getTimeColor(data.average_minutes)"
          >
            {{ formatTime(data.average_minutes) }}
          </div>
          <div class="text-xs text-n-slate-11">Média</div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1">
          <div class="text-xl font-bold text-n-blue-11">
            {{ formatTime(data.median_minutes) }}
          </div>
          <div class="text-xs text-n-slate-11">Mediana</div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1">
          <div class="text-xl font-bold text-n-amber-11">
            {{ formatTime(data.p90_minutes) }}
          </div>
          <div class="text-xs text-n-slate-11">P90</div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1">
          <div class="text-xl font-bold text-n-slate-12">
            {{ data.total_responses || 0 }}
          </div>
          <div class="text-xs text-n-slate-11">Total</div>
        </div>
      </div>

      <!-- Distribution -->
      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">Distribuição</span>
        <div class="flex flex-col gap-2">
          <div
            v-for="bucket in distribution"
            :key="bucket.label"
            class="flex items-center gap-2"
          >
            <span class="text-xs text-n-slate-11 w-20">{{ bucket.label }}</span>
            <div class="flex-1 h-5 bg-n-alpha-1 rounded overflow-hidden">
              <div
                class="h-full transition-all duration-300"
                :class="bucket.color ? '' : 'bg-n-blue-9'"
                :style="{
                  width: `${(bucket.count / maxDistribution) * 100}%`,
                  backgroundColor: bucket.color,
                }"
              />
            </div>
            <span class="text-xs text-n-slate-11 w-16 text-right">
              {{ bucket.count }} ({{ bucket.percentage }}%)
            </span>
          </div>
        </div>
      </div>

      <!-- Top Agents (fastest) -->
      <div v-if="byAgent.length > 0" class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">Agentes Mais Rápidos</span>
        <div class="flex flex-col gap-1">
          <div
            v-for="(agent, idx) in byAgent.slice(0, 5)"
            :key="agent.agent_id"
            class="flex items-center justify-between py-1 px-2 rounded bg-n-alpha-1"
          >
            <div class="flex items-center gap-2">
              <span class="text-xs text-n-slate-10">#{{ idx + 1 }}</span>
              <span class="text-sm text-n-slate-12">{{
                agent.agent_name
              }}</span>
            </div>
            <span
              class="text-sm font-medium"
              :class="getTimeColor(agent.avg_minutes)"
            >
              {{ formatTime(agent.avg_minutes) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Extremes -->
      <div
        class="flex items-center justify-between text-xs text-n-slate-11 pt-2 border-t border-n-weak"
      >
        <span>Mais rápido: {{ formatTime((data.min_seconds || 0) / 60) }}</span>
        <span>Mais lento: {{ formatTime((data.max_seconds || 0) / 60) }}</span>
      </div>
    </div>
  </div>
</template>
